import { defaultAiAssistantInfo } from "@memorang/helpers/src/aiAssistant";
import type { Apps } from "types/apps";
import { z } from "zod";

const tenantSchema = z.object({
	id: z.string(),
	name: z.string(),
});

const masteryLabelsSchema = z.object({
	low: z.string(),
	medium: z.string(),
	high: z.string(),
	none: z.string(),
});

const scoreRangeSchema = z.object({
	rangeArray: z.array(z.number()),
	range: z.string(),
	color: z.string(),
	icon: z.string(),
});

const fontSizesSchema = z.object({
	stem: z.number(),
	answer: z.number(),
});

const purchaseSchema = z.object({
	isSubscriptionEnabled: z.boolean().optional(),
	subheading: z.string().nullable().optional(),
});

const logoSchema = z.object({
	stacked: z.object({
		// todo: might be more complex schema for image
		light: z.object({
			uri: z.string(),
		}),
		dark: z.object({
			uri: z.string(),
		}),
	}),
	default: z.object({
		light: z.object({
			uri: z.string(),
		}),
		dark: z.object({
			uri: z.string(),
		}),
	}),
	company: z
		.object({
			light: z.object({
				uri: z.string(),
			}),
			dark: z.object({
				uri: z.string(),
			}),
		})
		.optional(),
});

const linksSchema = z.object({
	baseUrl: z.string().optional(),
	privacy: z.string(),
	tos: z.string(),
	ios: z.string().optional(),
	iosLocalised: z.record(z.string(), z.string().nullable()).optional(),
	android: z.string().optional(),
	androidLocalised: z.record(z.string(), z.string().nullable()).optional(),
	redeemSupportUrl: z.string().optional(),
	companyUrl: z.string().optional(),
	contactSupportUrl: z.string().optional(),
});

const revenueCatSchema = z.object({
	ios: z.string(),
	android: z.string(),
});

const appAnalyticsSchema = z.object({
	mixpanel: z.object({
		id: z.string(),
	}),
});

const clerkSchema = z
	.object({
		publishableKeyProd: z.string(),
		publishableKeyDev: z.string(),
	})
	.optional();
const appKeysSchema = z.object({
	revenueCat: revenueCatSchema,
	oneSignalAppId: z.string(),
	clerk: clerkSchema,
});

const elevenLabsSchema = z.object({
	modelId: z.string(),
	voiceId: z.string(),
});

const sessionViewConfigSchema = z.object({
	cta: z.string().optional(),
	maxItems: z
		.object({
			flashcards: z.number(),
			questions: z.number(),
		})
		.optional(),
	postAnswerStats: z
		.object({
			showXpEarned: z.boolean().optional(),
			showNumAttempts: z.boolean().optional(),
		})
		.optional(),
	timer: z.boolean().optional(),
	showCrossOut: z.boolean().optional().default(true),
	selectionStrategy: z
		.object({
			default: z.string(),
			optimized: z.boolean(),
			random: z.boolean(),
			shuffled: z.boolean(),
		})
		.optional(),
	suggestCorrection: z
		.object({
			description: z.string(),
		})
		.optional(),
});

const reportViewConfigSchema = z.object({
	practiceTest: z.object({
		reviewEnabled: z.boolean(),
	}),
});

export const onboardingDatItemSchema = z.object({
	title: z.string().optional(),
	image: z.string().optional(),
	type: z.string().optional(),
	id: z.string().optional(),
	description: z.string().optional(),
	label: z.string().optional(),
	icon: z.string().optional(),
});

export type OnboardingDataItem = z.infer<typeof onboardingDatItemSchema>;

const onboardingDataSchema = z.array(onboardingDatItemSchema);

const onboardingStepSchema = z.object({
	step: z.string(),
	enabled: z.boolean(),
	title: z.string().optional(),
	numMaxSelect: z.number().optional(),
	description: z.string().optional(),
	descriptionMobile: z.string().optional(),
	secondaryCta: z.string().optional(),
	isMobile: z.boolean().optional(),
	suffix: z.string().optional(),
	data: onboardingDataSchema.optional(),
	cta: z.string().optional(),
	graphics: z
		.object({
			desktop: z.string(),
			mobile: z.string().optional(),
		})
		.optional(),
	demo: z
		.object({
			desktop: z.string(),
			mobile: z.string().optional(),
		})
		.optional(),
});

export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

const navigationRouteSchema = z.object({
	title: z.string(),
	name: z.string(),
	icon: z.object({
		default: z.string(),
		selected: z.string(),
	}),
	enabled: z.boolean(),
	custom: z.boolean().optional(),
});

const navigationSchema = z.object({
	routes: z.object({
		main: z.array(navigationRouteSchema),
	}),
});

const oldPortalSchema = z.object({
	title: z.string(),
	description: z.string(),
	link: z.string(),
});

const aiAssistantSchema = z.object({
	title: z.string().optional().default(defaultAiAssistantInfo.title),
	alias: z.string().optional().default(defaultAiAssistantInfo.alias),
	icon: z.string(),
	showOriginalQuestion: z
		.boolean()
		.optional()
		.default(defaultAiAssistantInfo.showOriginalQuestion),
	initialMessage: z
		.string()
		.optional()
		.default(defaultAiAssistantInfo.initialMessage),
	initialAiMessage: z
		.record(z.string(), z.string())
		.optional()
		.default(defaultAiAssistantInfo.initialAiMessage),
});

const landingSchema = z.object({
	title: z
		.string()
		.or(
			z
				.object({
					prefix: z.string(),
					decoratedText: z.string(),
					suffix: z.string(),
				})
				.partial(),
		)
		.optional(),
	marketText: z
		.object({
			prefix: z.string().optional(),
			suffix: z.string().optional(),
		})
		.optional(),
	subtitle: z.string().optional(),
	valueProps: z
		.array(
			z.object({
				icon: z.string(),
				title: z.string(),
				subtitle: z.string(),
			}),
		)
		.optional(),
	landingGraphics: z.string().optional(),
});

const appConfigSchema = z.object({
	id: z.string(),
	name: z.string(),
	trademark: z.string().optional(),
	font: z.string().optional().default("opensans"),
	purchase: purchaseSchema.optional(),
	masteryLabels: masteryLabelsSchema.optional(),
	scoreRanges: z.array(scoreRangeSchema).optional(),
	fontSizes: fontSizesSchema.optional(),
	analytics: appAnalyticsSchema,
	logo: logoSchema,
	links: linksSchema,
	keys: appKeysSchema,
	supportUrl: z.string().optional(),
	oldPortal: oldPortalSchema.optional(),
	aiAssistant: aiAssistantSchema.optional().default(defaultAiAssistantInfo),
	isImageChallengeApp: z.boolean().optional().default(false),
	landing: landingSchema.optional(),
	showCountrySelection: z.boolean().optional().default(false),
	componentOverrides: z.record(z.string(), z.any()).optional(),
	elevenLabs: elevenLabsSchema.optional(),
	appAvailabilityRegions: z
		.array(z.string())
		.optional()
		.describe(
			"Array of ISO 3166-1 alpha-2 country codes where app is available. Empty or undefined means available everywhere",
		),
});

const themeSchema = z.record(z.string(), z.any());

export const customConfigTypeSchema = z.object({
	tenant: tenantSchema,
	supportEmail: z.string(),
	companyName: z.string(),
	app: appConfigSchema,
	groupByCategory: z.boolean().optional().nullable(),
	exam: z.object({
		enabled: z.boolean(),
		quizEnabled: z.boolean().optional(),
		allowChangeExam: z.boolean().optional(),
	}),
	store: z.object({
		enabled: z.boolean(),
	}),
	homeView: z
		.object({
			showRecommendedChallenge: z.boolean().optional(),
			showArticles: z.boolean().optional(),
			showChallengeOfTheWeek: z.boolean().optional(),
		})
		.optional()
		.nullable(),
	showStudyPackAsHome: z.boolean().optional().nullable(),
	reportView: reportViewConfigSchema.optional().nullable(),
	onboarding: z.array(onboardingStepSchema).optional().nullable(),
	navigation: navigationSchema,
	sessionView: sessionViewConfigSchema.optional().nullable(),
	theme: themeSchema,
});

export type CustomConfigType = z.infer<typeof customConfigTypeSchema>;

export type ConfigMap = Partial<Record<Apps, CustomConfigType>>;
